import type React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { MenuItemResponse } from "@/api/lib/db/types";
import type {
	CreateMenuItemInput,
	UpdateMenuItemInput,
} from "@/api/routes/menu/types";
import Icon from "../../../lib/components/custom/Icon";
import { Button } from "../../../lib/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../lib/components/ui/dialog";
import { useApi } from "../../../lib/hooks/api/use-api";
import MenuItemForm from "./MenuItemForm";

interface MenuItemDialogProps {
	mode: "create" | "edit";
	open: boolean;
	onOpenChange: (open: boolean) => void;
	item?: MenuItemResponse;
	categories: any[];
	onSuccess?: () => void;
	trigger?: React.ReactNode;
}

interface Size {
	sizeName: string;
	price: string;
	isAvailable: boolean;
}

export default function MenuItemDialog({
	mode,
	open,
	onOpenChange,
	item,
	categories,
	onSuccess,
	trigger,
}: MenuItemDialogProps) {
	const { menu, uploads } = useApi();

	const [formData, setFormData] = useState<
		CreateMenuItemInput & { isAvailable?: boolean }
	>({
		name: "",
		displayId: "",
		description: "",
		category: "",
		imageUrl: "",
		isVegan: false,
		isVegetarian: false,
		rating: 5,
		discount: 0,
		isAvailable: true,
	});

	const [sizes, setSizes] = useState<Size[]>([]);
	const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
	const [selectedImagePreview, setSelectedImagePreview] = useState<
		string | null
	>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [errors, setErrors] = useState<
		Partial<Record<keyof CreateMenuItemInput, string>>
	>({});

	const selectedCategoryName = formData.category;
	const selectedCategory = categories.find(
		(c) =>
			(typeof c === "string" ? c : c.name || c.slug) === selectedCategoryName,
	);
	const selectedCategoryId = selectedCategory?.id;

	const { data: categorySizes } = menu.getCategorySizes({
		id: selectedCategoryId ? String(selectedCategoryId) : "",
	});
	const { data: itemDetails } = menu.getItemById(
		mode === "edit" && item?.id ? Number(item.id) : 0,
	);

	useEffect(() => {
		if (open) {
			if (mode === "edit" && item) {
				setFormData({
					name: item.name,
					displayId: item.displayId || "",
					description: item.description || "",
					category: item.category,
					imageUrl: item.imageUrl || "",
					isVegan: item.isVegan || false,
					isVegetarian: item.isVegetarian || false,
					rating: Number(item.rating) || 5,
					discount: Number(item.discount) || 0,
					isAvailable: item.isAvailable ?? true,
				});
				setSelectedImagePreview(item.imageUrl || null);
			} else if (mode === "create") {
				const defaultCategory =
					categories.length > 0
						? typeof categories[0] === "string"
							? categories[0]
							: categories[0].name
						: "";
				setFormData({
					name: "",
					displayId: "",
					description: "",
					category: defaultCategory,
					imageUrl: "",
					isVegan: false,
					isVegetarian: false,
					rating: 5,
					discount: 0,
					isAvailable: true,
				});
				setSelectedImagePreview(null);
			}
			setSelectedImageFile(null);
			setErrors({});
		}
	}, [open, mode, item, categories]);

	useEffect(() => {
		return () => {
			if (selectedImagePreview && selectedImagePreview.startsWith("blob:")) {
				URL.revokeObjectURL(selectedImagePreview);
			}
		};
	}, [selectedImagePreview]);

	useEffect(() => {
		if (!open) return;

		if (categorySizes && categorySizes.length > 0) {
			if (mode === "create") {
				const initialSizes = categorySizes.map((sizeName: string) => ({
					sizeName,
					price: "",
					isAvailable: true,
				}));
				setSizes(initialSizes);
			} else if (mode === "edit" && itemDetails && itemDetails.sizes) {
				const mergedSizes = categorySizes.map((sizeName: string) => {
					const existing = itemDetails.sizes.find(
						(s: any) => s.sizeName === sizeName,
					);
					if (existing) {
						return {
							sizeName,
							price: existing.price,
							isAvailable: !!existing.isAvailable,
						};
					}
					return {
						sizeName,
						price: "",
						isAvailable: false,
					};
				});
				setSizes(mergedSizes);
			}
		} else if (mode === "create") {
			setSizes([]);
		}
	}, [categorySizes, itemDetails, mode, open]);

	const handleFormChange = (field: string, value: any) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field as keyof CreateMenuItemInput]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const handleImageSelect = (fileOrUrl: File | string) => {
		if (fileOrUrl instanceof File) {
			setSelectedImageFile(fileOrUrl);
			const url = URL.createObjectURL(fileOrUrl);
			setSelectedImagePreview(url);
			setFormData((prev) => ({ ...prev, imageUrl: "" }));
		} else {
			setSelectedImageFile(null);
			setSelectedImagePreview(fileOrUrl);
			setFormData((prev) => ({ ...prev, imageUrl: fileOrUrl }));
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof CreateMenuItemInput, string>> = {};
		if (!formData.name.trim()) newErrors.name = "Item name is required";
		if (!formData.displayId?.trim())
			newErrors.displayId = "Display ID is required";
		if (!formData.category) newErrors.category = "Category is required";
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;
		const activeValidSizes = sizes.filter(
			(s) => s.isAvailable && s.price && parseFloat(s.price) > 0,
		);

		if (activeValidSizes.length === 0 && mode === "create") {
			toast.error("Please set a price for at least one size");
			return;
		}

		setIsSubmitting(true);

		try {
			if (mode === "create") {
				const newItem = await menu.createItem.mutateAsync(formData);
				if (newItem.item?.id) {
					await Promise.all(
						activeValidSizes.map((size) =>
							menu.createSize.mutateAsync({
								menuItemId: newItem.item.id as number,
								sizeName: size.sizeName,
								price: parseFloat(size.price),
								isAvailable: size.isAvailable,
							}),
						),
					);
				}

				if (selectedImageFile && newItem.item?.id) {
					try {
						await uploads.uploadMenuImage.mutateAsync({
							menuItemId: newItem.item.id as number,
							image: selectedImageFile,
						});
					} catch (uploadError) {
						console.error("Failed to upload image:", uploadError);
						toast.warning("Item created but image upload failed.");
					}
				}
				toast.success("Menu item created successfully");
				onOpenChange(false);
				onSuccess?.();
			} else if (mode === "edit" && item) {
				const sizesToUpdate = sizes
					.filter((s) => s.price && parseFloat(s.price) > 0)
					.map((s) => ({
						sizeName: s.sizeName,
						price: parseFloat(s.price),
						isAvailable: s.isAvailable,
					}));

				const updateData: UpdateMenuItemInput = {
					name: formData.name,
					description: formData.description,
					isVegan: formData.isVegan,
					isVegetarian: formData.isVegetarian,
					isAvailable: formData.isAvailable,
					imageUrl: formData.imageUrl || undefined,
					sizes: sizesToUpdate,
				};

				await menu.updateItem.mutateAsync({ id: item.id, data: updateData });

				if (selectedImageFile) {
					await uploads.uploadMenuImage.mutateAsync({
						menuItemId: item.id,
						image: selectedImageFile,
					});
				}
				toast.success("Menu item updated successfully");
				onOpenChange(false);
				onSuccess?.();
			}
		} catch (error) {
			console.error(error);
			toast.error(
				mode === "create"
					? "Failed to create menu item"
					: "Failed to update menu item",
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const dialogContent = (
		<DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
			<DialogHeader className="space-y-3">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Icon
							name={mode === "create" ? "Plus" : "Settings"}
							className="w-5 h-5 text-primary"
						/>
					</div>
					<DialogTitle className="text-xl font-manrope">
						{mode === "create" ? "Add Menu Item" : "Edit Menu Item"}
					</DialogTitle>
				</div>
			</DialogHeader>

			<MenuItemForm
				formData={formData}
				onChange={handleFormChange}
				errors={errors}
				categories={categories}
				mode={mode}
				onImageSelect={handleImageSelect}
				selectedImagePreview={selectedImagePreview}
				sizes={sizes}
				onSizesChange={setSizes}
			/>

			<div className="flex justify-end gap-3 pt-4 border-t">
				<Button
					variant="outline"
					onClick={() => onOpenChange(false)}
					className="px-6"
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="primary"
					disabled={isSubmitting}
					className="px-6 shadow-lg"
				>
					{isSubmitting ? (
						<>
							<Icon name="Loader" className="w-4 h-4 animate-spin" />
							{mode === "create" ? "Creating..." : "Updating..."}
						</>
					) : (
						<>
							<Icon
								name={mode === "create" ? "Plus" : "Check"}
								className="w-4 h-4"
							/>
							{mode === "create" ? "Create Item" : "Save Changes"}
						</>
					)}
				</Button>
			</div>
		</DialogContent>
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
			{dialogContent}
		</Dialog>
	);
}
